import { useMutation } from "@tanstack/react-query";
import { CourseGroup } from "../../../learner/types";
import { executeQuery } from "../../../utils";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";

const OrderButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
  direction: "up" | "down";
}> = ({ onClick, disabled, direction }) => (
  <button
    className="flex h-6 w-6 items-center justify-center rounded border border-dm-charcoal-100 hover:border-dm-charcoal-200 disabled:cursor-not-allowed disabled:text-black/30 disabled:hover:border-dm-charcoal-100"
    onClick={onClick}
    disabled={disabled}
    title={direction === "up" ? "Move this group up" : "Move this group down"}
  >
    {direction === "up" ? "↑" : "↓"}
  </button>
);

export const CourseGroupOrderCell: React.FC<{
  rowIndex: number;
  rows: CourseGroup[];
  onReorder: () => void;
}> = ({ rowIndex, rows, onReorder }) => {
  const toastContext = useDeltaToastContext();

  const { mutate, isPending } = useMutation({
    mutationFn: (orders: Record<string, number>) =>
      executeQuery({
        path: "/manager_new/parent-portal/course-groups/order",
        method: "post",
        requestBody: { orders },
      }),
  });

  const swap = (a: number, b: number) => {
    const groupA = rows[a];
    const groupB = rows[b];
    mutate(
      {
        [groupA._id]: groupB.order,
        [groupB._id]: groupA.order,
      },
      {
        onSuccess: () => onReorder(),
        onError: (e) => {
          toastContext.addToast({
            status: "Error",
            message:
              "There was an error updating the order of the course groups",
          });
        },
      }
    );
  };

  return (
    <span className="flex gap-2">
      <OrderButton
        onClick={() => swap(rowIndex - 1, rowIndex)}
        disabled={rowIndex === 0 || isPending}
        direction="up"
      />
      <OrderButton
        onClick={() => swap(rowIndex, rowIndex + 1)}
        disabled={rowIndex === rows.length - 1 || isPending}
        direction="down"
      />
    </span>
  );
};
